import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const initialState = {
  auditableTypes: [],
  audits: [],
  isLoading: false,
  isDownloading: false,
  error: null,
};

export const getAuditableTypes = createAsyncThunk(
  "audits/getAuditableTypes",
  async ({ selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/audits?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data.auditable_types;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const searchAudits = createAsyncThunk(
  "audits/searchAudits",
  async ({ auditableTypes, timeFrame, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/audits/search?auditable_types=${auditableTypes.join(",")}&time_frame=${timeFrame}&account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const downloadAudits = createAsyncThunk(
  "audits/downloadAudits",
  async ({ auditableTypes, timeFrame, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/audits/download?auditable_types=${auditableTypes.join(",")}&time_frame=${timeFrame}&account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `audit_history_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Audit history downloaded successfully");
      return true;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const auditSlice = createSlice({
  name: "audit",
  initialState,
  reducers: {
    clearAudits: (state) => {
      state.audits = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAuditableTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAuditableTypes.fulfilled, (state, action) => {
        state.auditableTypes = ["all", ...action.payload];
        state.isLoading = false;
      })
      .addCase(getAuditableTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error("Failed to fetch auditable types");
      })
      .addCase(searchAudits.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchAudits.fulfilled, (state, action) => {
        state.audits = action.payload;
        state.isLoading = false;
      })
      .addCase(searchAudits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error("Failed to fetch audit records");
      })
      .addCase(downloadAudits.pending, (state) => {
        state.isDownloading = true;
        state.error = null;
      })
      .addCase(downloadAudits.fulfilled, (state) => {
        state.isDownloading = false;
      })
      .addCase(downloadAudits.rejected, (state, action) => {
        state.isDownloading = false;
        state.error = action.payload;
        toast.error("Failed to download audit history");
      });
  },
});

export const { clearAudits } = auditSlice.actions;

export default auditSlice.reducer;